import React from "react";
import InputField from "../../SharedComponent/Fields/InputField";
import SelectField from "../../SharedComponent/Fields/SelectField";
import ContractHistoryTable from "../ContractHistoryTable";

const ContractInfoSection = ({
  t,
  contractForm,
  handleChange,
  warehouses,
  selectedContractWarehouse,
  handleWarehouseChange2,
  checboxValue,
  handleCheckboxToggle,
  id,
  refreshContracts,
  transportUrl,
  isValidUrl,
  normalizedTransportUrl,
}) => {
  const contract = contractForm || {};
  const customer = contract.customer || {};

  const url =
    transportUrl ||
    contract.transport_order_url ||
    contractForm?.transport_order_url ||
    "";

  // Helper for URL validation if not passed as prop
  const isUrlValid =
    isValidUrl ||
    ((url) => {
      if (!url) return false;
      try {
        new URL(url);
        return true;
      } catch {
        try {
          new URL(`https://${url}`);
          return true;
        } catch {
          return false;
        }
      }
    });

  // Helper for normalization if not passed as prop
  const normUrl =
    normalizedTransportUrl ||
    (() => {
      if (!url) return "";
      try {
        new URL(url);
        return url;
      } catch {
        return `https://${url}`;
      }
    })();

  return (
    <>
      <div className="mb-8 bg-white p-4 shadow-md">
        <h4 className="text-md font-medium mb-3 text-gray-700">
          {t("assetPageHeadings.contractInformation")}
        </h4>

        {/* Contract Details */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-2 mb-2">
          <SelectField
            name="contract_type"
            value={contract.contract_type || ""}
            onChange={handleChange}
            label={t("createAsset.placeholders.contractLabel")}
            explanation={t("createAsset.placeholders.contractExplanation")}
            required
            menus={[
              {
                value: "",
                option: t("createAsset.placeholders.contractExplanation"),
              },
              { value: "demo", option: t("assets.options.demo") },
              { value: "rental", option: t("assets.options.rental") },
              { value: "leasing", option: t("assets.options.leasing") },
              { value: "sale", option: t("assets.options.sale") },
              { value: "maintenance", option: t("assets.options.maintenance") },
            ]}
          />
          <InputField
            name="contract_start"
            type="date"
            value={contract.contract_start || ""}
            onChange={handleChange}
            label={t("createAsset.placeholders.contractStartLabel")}
            explanation={t("createAsset.placeholders.contractStartExplanation")}
            required
          />
          <InputField
            name="contract_end"
            type="date"
            value={contract.contract_end || ""}
            onChange={handleChange}
            label={t("createAsset.placeholders.contractEndLabel")}
            explanation={t("createAsset.placeholders.contractEndExplanation")}
            required
          />
          <InputField
            name="contract_price_euro"
            type="number"
            step="0.01"
            min="0"
            value={contract.contract_price_euro || ""}
            onChange={handleChange}
            maxLength={12}
            label={t("createAsset.placeholders.contractPrice")}
            placeholder={t("createAsset.placeholders.contractPricePlaceholder")}
            explanation={t("createAsset.placeholders.contractPriceExplanation")}
          />
        </div>

        {/* Customer Info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-x-2 mb-2">
          <InputField
            name="customer_company"
            type="text"
            required
            maxLength={50}
            value={customer.customer_company || ""}
            onChange={handleChange}
            placeholder={t("createAsset.placeholders.companyPlaceholder")}
            label={t("createAsset.placeholders.companyLabel")}
            explanation={t("createAsset.placeholders.companyExplanation")}
          />
          <InputField
            name="name"
            type="text"
            maxLength={50}
            value={customer.name || ""}
            required
            onChange={handleChange}
            placeholder={t("createAsset.placeholders.contactPersonPlaceholder")}
            label={t("createAsset.placeholders.contactPersonLabel")}
            explanation={t("createAsset.placeholders.contactPersonExplanation")}
          />
          <InputField
            name="phone_number"
            type="tel"
            maxLength={20}
            required
            value={customer.phone_number || ""}
            onChange={handleChange}
            placeholder={t("createAsset.placeholders.phonePlaceholder")}
            label={t("createAsset.placeholders.phoneLabel")}
            explanation={t("createAsset.placeholders.phoneExplanation")}
          />
          <InputField
            name="email"
            type="email"
            maxLength={100}
            value={customer.email || ""}
            onChange={handleChange}
            placeholder={t("createAsset.placeholders.emailPlaceholder")}
            label={t("createAsset.placeholders.emailLabel")}
            explanation={t("createAsset.placeholders.emailExplanation")}
          />
          <InputField
            name="customer_number"
            type="text"
            maxLength={20}
            value={customer.customer_number || ""}
            onChange={handleChange}
            placeholder={t(
              "createAsset.placeholders.customerNumberPlaceholder"
            )}
            label={t("createAsset.placeholders.customerNumberLabel")}
            explanation={t(
              "createAsset.placeholders.customerNumberExplanation"
            )}
          />
        </div>

        {/* Invoice Address */}
        <div className="mb-4">
          <h5 className="text-sm font-medium mb-2 text-gray-600">
            {t("createAsset.placeholders.InvoiceAddress")}
          </h5>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-x-2">
            {Array.isArray(warehouses) && warehouses?.length > 0 && (
              <div className="col-span-2">
                <SelectField
                  name="warehouse"
                  value={selectedContractWarehouse?.id || ""}
                  onChange={(e) => {
                    handleWarehouseChange2(e);
                  }}
                  label={t("createAsset.placeholders.warehouseLabel")}
                  explanation={t(
                    "createAsset.placeholders.warehouseExplanation"
                  )}
                  placeholder={t(
                    "createAsset.placeholders.warehousePlaceholder"
                  )}
                  menus={[
                    {
                      value: "",
                      option: t(
                        "createAsset.placeholders.warehousePlaceholder"
                      ),
                    },
                    ...warehouses.map((warehouse) => {
                      return {
                        value: warehouse.id,
                        option: warehouse.name,
                      };
                    }),
                  ]}
                />
              </div>
            )}
            <div>
              <InputField
                name="invoice_address_street"
                type="text"
                maxLength={20}
                value={contract.invoice_address_street || ""}
                onChange={handleChange}
                placeholder={t("createAsset.placeholders.streetPlaceholder")}
                label={t("createAsset.placeholders.streetLabel")}
                explanation={t("createAsset.placeholders.streetExplanation")}
                required
              />
            </div>
            <div>
              <InputField
                name="invoice_address_city"
                type="text"
                maxLength={20}
                value={contract.invoice_address_city || ""}
                onChange={handleChange}
                placeholder={t("createAsset.placeholders.cityPlaceholder")}
                label={t("createAsset.placeholders.cityLabel")}
                explanation={t("createAsset.placeholders.cityExplanation")}
                required
              />
            </div>
            <div>
              <InputField
                name="invoice_address_country"
                type="text"
                maxLength={20}
                value={contract.invoice_address_country || ""}
                onChange={handleChange}
                placeholder={t("createAsset.placeholders.countryPlaceholder")}
                label={t("createAsset.placeholders.countryLabel")}
                explanation={t("createAsset.placeholders.countryExplanation")}
                required
              />
            </div>
            <div>
              <InputField
                name="invoice_address_zip"
                type="text"
                maxLength={7}
                value={contract.invoice_address_zip || ""}
                onChange={handleChange}
                placeholder={t("createAsset.placeholders.zipPlaceholder")}
                label={t("createAsset.placeholders.zipLabel")}
                explanation={t("createAsset.placeholders.zipExplanation")}
                required
              />
            </div>
          </div>
        </div>

        {/* Delivery Address */}
        <div className="">
          <div className="flex flex-col gap-2">
            <div>
              <h5 className="text-sm font-medium text-gray-600">
                {t("createAsset.placeholders.deliveryAddress")}
              </h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-2 items-end">
                {/* Checkbox column */}
                <div className="col-span-1 sm:col-span-2 md:col-span-1 lg:col-span-1">
                  <label className="flex items-center h-10">
                    <input
                      type="checkbox"
                      name=""
                      checked={checboxValue}
                      onChange={handleCheckboxToggle}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">
                      {t("createAsset.placeholders.sameAsInvoice")}
                    </span>
                  </label>
                </div>

                {/* Street field */}
                <div>
                  <InputField
                    name="delivery_address_street"
                    type="text"
                    maxLength={20}
                    disabled={checboxValue}
                    value={
                      checboxValue
                        ? contract.invoice_address_street || ""
                        : contract.delivery_address_street || ""
                    }
                    onChange={handleChange}
                    placeholder={t(
                      "createAsset.placeholders.streetPlaceholder"
                    )}
                    label={t("createAsset.placeholders.streetLabel")}
                    explanation={t(
                      "createAsset.placeholders.streetExplanation"
                    )}
                    required
                  />
                </div>

                {/* City field */}
                <div>
                  <InputField
                    name="delivery_address_city"
                    type="text"
                    maxLength={20}
                    disabled={checboxValue}
                    value={
                      checboxValue
                        ? contract.invoice_address_city || ""
                        : contract.delivery_address_city || ""
                    }
                    onChange={handleChange}
                    placeholder={t("createAsset.placeholders.cityPlaceholder")}
                    label={t("createAsset.placeholders.cityLabel")}
                    explanation={t("createAsset.placeholders.cityExplanation")}
                    required
                  />
                </div>

                {/* Country field */}
                <div>
                  <InputField
                    name="delivery_address_country"
                    type="text"
                    maxLength={20}
                    disabled={checboxValue}
                    value={
                      checboxValue
                        ? contract.invoice_address_country || ""
                        : contract.delivery_address_country || ""
                    }
                    onChange={handleChange}
                    placeholder={t(
                      "createAsset.placeholders.countryPlaceholder"
                    )}
                    label={t("createAsset.placeholders.countryLabel")}
                    explanation={t(
                      "createAsset.placeholders.countryExplanation"
                    )}
                    required
                  />
                </div>

                {/* ZIP field */}
                <div>
                  <InputField
                    name="delivery_address_zip"
                    type="number"
                    maxLength={7}
                    disabled={checboxValue}
                    value={
                      checboxValue
                        ? contract.invoice_address_zip || ""
                        : contract.delivery_address_zip || ""
                    }
                    onChange={handleChange}
                    placeholder={t("createAsset.placeholders.zipPlaceholder")}
                    label={t("createAsset.placeholders.zipLabel")}
                    explanation={t("createAsset.placeholders.zipExplanation")}
                    required
                  />
                  
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* ADD TRANSPORT ORDER URL BLOCK HERE */}
        {/* {url && isUrlValid(url) && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-x-2 mb-3 mt-4">
            <div className="lg:col-span-4">
              <div className="flex items-center gap-2">
                <span className="text-md font-medium text-gray-700">
                  {t("createAsset.placeholders.transportUrlLabel") ||
                    "Transport Order URL"}
                  :
                </span>
                <a
                  href={normUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline break-all"
                  title={normUrl}
                >
                  {url}
                </a>
              </div>
            </div>
          </div>
        )} */}
      </div>
      <ContractHistoryTable t={t} id={id} refreshContracts={refreshContracts} transportUrl={transportUrl} isValidUrl={isValidUrl} />
    </>
  );
};

export default ContractInfoSection;
